<template>
  <WdsModal :show="show" :title="$t('Retry Webhook Event')" @cancel="show = false">
    <WdsStack
      justify-center
      align-center
      direction-column
      gap-md
      class="content"
      data-test="modal-retry-webhook-event"
    >
      <div class="icon-outer">
        <div class="icon-inner">
          <WdsIcon name="send" color="#D12223" :size="24"></WdsIcon>
        </div>
      </div>
      <WdsText large>
        {{ $t('Are you sure you want to retry sending this webhook event?') }}
      </WdsText>
      <WdsText secondary>
          {{ $t('This will also run any actions configured on the recipient end.') }}
      </WdsText>
      <WdsStack justify-space-between align-center class="tw-w-full">
        <WdsButton :disabled="loading" @click="show = false">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton
          :disabled="loading"
          error
          data-test="retry-webhook-event-button"
          @click.prevent="retryWebhookEvent"
        >
          {{ $t('Retry') }}
          <WdsSpinner v-if="loading" indeterminate class="tw-m-0.5" />
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsStack,
  WdsIcon,
  WdsModal,
  WdsButton,
  WdsSpinner,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, useFragment } from '@/gql'
import { WebhookEventLogFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import { retryWebhookEventMutation } from '@/components/Integrations/Webhooks/graphql/mutations'
import { Notification, NetworkError } from '@/utils/notification'
import useApolloError from '@/composables/useApolloError'

const clientId = 'publicApi'

const show = defineModel<boolean>('show')

const webhookEventLog = defineModel<FragmentType<typeof WebhookEventLogFragment>>('details')

const webhookEventLogExtracted = computed(() => useFragment(WebhookEventLogFragment, webhookEventLog.value))

const webhookEventId = (webhookEventLogData) => webhookEventLogData.value.webhookEvent.id

const { mutate, loading, error } = useMutation(retryWebhookEventMutation, {
  clientId,
  refetchQueries: ['WebhookEventLogs'],
})

const { validationErrors } = useApolloError(error)

const retryWebhookEvent = async () => {
  try {
    await mutate({ input: { id: webhookEventId(webhookEventLogExtracted) } })
    show.value = false
    new Notification({msg: $t('Webhook sent succesfully')}).dispatch()
  } catch (ex: any) {
    const msg = validationErrors.value?.['input.id'] ?? $t('Could not send webhook: Network error')
    new NetworkError({ ex, msg }).dispatch()
  }
}
</script>

<style lang="scss" scoped>
.content {
  :deep(strong) {
    color: black;
  }

  .icon-outer {
    border-radius: 50%;
    background-color: #fdf2f2;
    padding: 6px;
    .icon-inner {
      padding: 5px;
      border-radius: 50%;
      background-color: #f9e0e0;
    }
  }
}
</style>
